import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addPlayer, addPlayerCampaign, assignLicenceToPlayer, getPlayerDetails } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { FormSchemaType, PlayerMetaForm } from "./form/PlayerMetaForm";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { ROUTES } from "@/constants/routeConstants";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { BaseFormLayout } from "../shared/BaseFormLayout";

export const AddNewPlayerPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  useBreadcrumbs([
    {
      name: t("entities.player"),
      path: ROUTES.PLAYER,
    },
    {
      name: t("common.addNewEntity", { entity: t("entities.player") }),
      path: ROUTES.PLAYER_ADD,
    },
  ]);

  const { goToPlayerDetail } = useAppNavigate();
  const queryClient = useQueryClient();
  const addPlayerMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      const response = await addPlayer({
        name: values.name,
        screenSizeId: values.screenSizeId,
        firmGuid: String(activeFirm?.firmGuid),
        timeZone: values.timeZone,
        channelId: values.channelId,
        contentGroupId: values.contentGroupId,
        contentId: values.contentId,
        defaultContentId: values.defaultContentId,
        areaId: values.areaId,
        latitude: values.latitude,
        longitude: values.longitude,
        healthSequence: values.healthSequence,
      });

      const playerDetails = await getPlayerDetails(response.data.newDataId, String(activeFirm?.firmGuid));

      if (values.campaignIds) {
        if (playerDetails) {
          for (const campaignId of values.campaignIds) {
            await addPlayerCampaign({
              playerId: playerDetails.data.intId,
              campaignId,
              firmGuid: String(activeFirm?.firmGuid),
            });
          }
        }
      }

      if (values.firmLicenceintId) {
        await assignLicenceToPlayer({
          firmGuid: String(activeFirm?.firmGuid),
          playerId: playerDetails.data.id,
          firmLicenceId: values.firmLicenceintId,
        });
      }

      return Promise.resolve({ newDataId: response.data.newDataId });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["getPlayer"],
      });
      showSuccess(t("common.addedSuccess"));
      goToPlayerDetail(data.newDataId);
    },
    onError: showApiError,
  });

  return (
    <BaseFormLayout title={t("common.addNewEntity", { entity: t("entities.player") })}>
      <PlayerMetaForm
        buttonText={t("common.add")}
        onSubmit={(values) => addPlayerMutation.mutate(values)}
        isLoading={addPlayerMutation.isPending}
      />
    </BaseFormLayout>
  );
};
